import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { PricingCard } from "@/components/PricingCard";
import { Faq } from "@/components/Faq";
import { GlassPanel } from "@/components/GlassPanel";
import { pricingTiers } from "@/content/pricing";
import { billingFaqs } from "@/content/faqs";
import { routeSeo } from "@/content/seo";

export function PricingPage() {
  const seo = routeSeo["/pricing"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/pricing" />

      <section className="container page-hero">
        <p className="eyebrow">Pricing</p>
        <h1 className="page-hero-title">Six roles. One that fits you, in under a minute.</h1>
        <p className="page-hero-lede">
          Creator to learn, Builder for more room to work, Professional with no workspace limit,
          Studio and Team to collaborate at different scales, Enterprise to govern at scale. Each
          plan is a role in the Devory ecosystem, not a bundle of arbitrarily split features.
          Professional is marked below because it is the plan most people building commercial work
          should start with, not because of invented usage data. Prices shown are the real, final
          V1 prices, and checkout is live -- sign up, choose a plan from Settings &gt; Billing
          inside Devory Cloud, and pay through Stripe.
        </p>
      </section>

      <section className="container section-tight" aria-labelledby="boundary-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={
              <span id="boundary-heading">What the paid boundary actually is today</span>
            }
          />
          <p>
            The full core Devory experience -- creating, editing, saving and reloading projects --
            is identical on every tier, including Creator. What a paid plan buys in V1 is{" "}
            <strong>room to work</strong>: Creator is capped at 1 private workspace, Builder raises
            that to 5, Professional removes the cap entirely, Studio opens that same unlimited
            workspace access to a 5-member organisation, and Team raises the organisation to 10
            members -- plus a monthly allowance of scoped cloud workflow execution (input, output,
            constant and logger components only, not arbitrary code) that rises with plan.
            Marketplace publishing is separate from plan tier entirely -- it&rsquo;s free at every
            plan, including Creator. Export/deployment and broader AI cloud compute are direction
            Devory is still building toward -- see each plan&rsquo;s &ldquo;Coming later&rdquo;
            section below. Devory&rsquo;s contracting entity is not currently VAT-registered, so no
            VAT is added to the prices shown. If that changes, this page will be updated before it
            takes effect.
          </p>
        </GlassPanel>
      </section>

      <section className="container section" aria-labelledby="plans-heading">
        <h2 id="plans-heading" className="visually-hidden">
          Plans
        </h2>
        <div className="grid-6">
          {pricingTiers.map((tier) => (
            <PricingCard key={tier.id} tier={tier} />
          ))}
        </div>
      </section>

      <section className="container section-tight" aria-labelledby="billing-faq-heading">
        <GlassPanel className="problem-card">
          <SectionHeading level={2} title={<span id="billing-faq-heading">Billing FAQ</span>} />
          <Faq items={billingFaqs} />
        </GlassPanel>
      </section>
    </>
  );
}
